import { DsModalRef } from '@levelaccess/design-system';

import { $flaw } from '@shared/constants/flaw';
import { IAutomatedScanIssue } from '@shared/interfaces/scan-issues.interface';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { CommonUtility } from '@client/app/utility/common.utility';
import {
  FindingNavigationDirection,
  IFindingModalNavigation,
} from '@client/app/pages/public/scans/scan-results/issues-view-details-base/issues-view-details-modal.interface';

export interface IFindingModalNavigationHandlers {
  getIssues: () => IAutomatedScanIssue[];
  getCurrentIndex: (issues?: IAutomatedScanIssue[]) => number;
  selectAtIndex: (index: number, issues?: IAutomatedScanIssue[]) => void;
  getSelectedIssue: () => IAutomatedScanIssue;
  updateModalFinding: (modalRef: DsModalRef, issue: IAutomatedScanIssue, direction: FindingNavigationDirection) => void;
}

export function refreshFindingModalNavigationFlags(
  navigation: IFindingModalNavigation,
  handlers: IFindingModalNavigationHandlers,
  issues?: IAutomatedScanIssue[],
): void {
  const issuesList: IAutomatedScanIssue[] = issues ?? handlers.getIssues();
  const index: number = handlers.getCurrentIndex(issuesList);
  navigation.canGoPrevious = index > 0;
  navigation.canGoNext = index >= 0 && index < issuesList.length - 1;
}

export function navigateFindingInModal(
  modalRef: DsModalRef,
  navigation: IFindingModalNavigation,
  direction: FindingNavigationDirection,
  handlers: IFindingModalNavigationHandlers,
): void {
  const issues: IAutomatedScanIssue[] = handlers.getIssues();
  const currentIndex: number = handlers.getCurrentIndex(issues);
  const nextIndex: number = direction === 'previous' ? currentIndex - 1 : currentIndex + 1;
  if (nextIndex < 0 || nextIndex >= issues.length) {
    return;
  }

  handlers.selectAtIndex(nextIndex, issues);
  handlers.updateModalFinding(modalRef, handlers.getSelectedIssue(), direction);
  refreshFindingModalNavigationFlags(navigation, handlers, issues);
}

export function createFindingModalNavigation(
  modalRef: DsModalRef,
  handlers: IFindingModalNavigationHandlers,
  issues?: IAutomatedScanIssue[],
): IFindingModalNavigation {
  const navigation: IFindingModalNavigation = {
    canGoPrevious: false,
    canGoNext: false,
    goToPrevious: (): void => {
      navigateFindingInModal(modalRef, navigation, 'previous', handlers);
    },
    goToNext: (): void => {
      navigateFindingInModal(modalRef, navigation, 'next', handlers);
    },
  };

  refreshFindingModalNavigationFlags(navigation, handlers, issues);
  return navigation;
}

export function focusIssueViewButton(selectedIssue: IAutomatedScanIssue, rowIndexOnPage: number): void {
  if (SharedCommonUtility.isNullish(selectedIssue)) {
    return;
  }

  const buttonId: string = `details-btn-${selectedIssue[$flaw._id]}`;
  window.setTimeout((): void => {
    let element: HTMLElement | null = document.getElementById(buttonId);
    if (SharedCommonUtility.isNullish(element) && rowIndexOnPage >= 0) {
      const rows: NodeListOf<Element> = document.querySelectorAll('ds-table-component tbody tr.ds-table-row');
      const row: Element | undefined = rows[rowIndexOnPage];
      element = row?.querySelector('ds-table-cell-button button') as HTMLElement | null;
      if (SharedCommonUtility.notNullish(element) && !element.id) {
        element.id = buttonId;
      }
    }

    if (SharedCommonUtility.notNullish(element)) {
      CommonUtility.setFocusToElement(null, element);
    } else {
      CommonUtility.setFocusToElement(buttonId);
    }
  });
}
